import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { auth } from '../firebaseConfig';
import { openPublishSessionForWallet } from '../utils/auth';
import {
    followSpace,
    getFollowerCount,
    getIsFollowing,
    normalizeFollowPublicKey,
    unfollowSpace,
} from '../utils/follows';
import './MuultiStyle.css';

export default function SpaceFollowButton({
                                              targetPublicKey,
                                              targetMuultiName = '',
                                              showFollowerCount = false,
                                              followerMuultiName = '',
                                              className = '',
                                              compact = false,
                                              onFollowChange,
                                          }) {
    const wallet = useWallet();
    const { connected, publicKey } = wallet;
    const [isFollowing, setIsFollowing] = useState(false);
    const [followerCount, setFollowerCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const targetKey = normalizeFollowPublicKey(targetPublicKey);
    const walletStr = useMemo(() => {
        try {
            return normalizeFollowPublicKey(publicKey?.toBase58?.() || publicKey?.toString?.() || '');
        } catch {
            return '';
        }
    }, [publicKey]);
    const isSelf = Boolean(walletStr && targetKey && walletStr === targetKey);

    const refreshState = useCallback(async () => {
        if (!targetKey) return;

        setLoading(true);
        setError('');
        try {
            const [nextCount, nextIsFollowing] = await Promise.all([
                showFollowerCount ? getFollowerCount(targetKey) : Promise.resolve(null),
                walletStr && !isSelf
                    ? getIsFollowing({ followerPublicKey: walletStr, targetPublicKey: targetKey })
                    : Promise.resolve(false),
            ]);
            setFollowerCount(nextCount);
            setIsFollowing(nextIsFollowing);
        } catch (err) {
            console.error('Follow state load failed:', err);
            setError('Follow state unavailable.');
        } finally {
            setLoading(false);
        }
    }, [isSelf, showFollowerCount, targetKey, walletStr]);

    useEffect(() => {
        refreshState();
    }, [refreshState]);

    const handleToggleFollow = async () => {
        if (!targetKey || isSelf) return;
        setError('');

        if (!connected || !walletStr) {
            setError('Connect a wallet to follow spaces.');
            return;
        }

        setSaving(true);
        try {
            if (auth?.currentUser?.uid !== walletStr) {
                await openPublishSessionForWallet(wallet);
            }
            const authUid = auth?.currentUser?.uid || '';

            if (authUid !== walletStr) {
                throw new Error('Auth UID does not match wallet public key.');
            }

            if (isFollowing) {
                await unfollowSpace({
                    followerPublicKey: walletStr,
                    targetPublicKey: targetKey,
                });
                setIsFollowing(false);
                setFollowerCount((count) => (
                    typeof count === 'number' ? Math.max(0, count - 1) : count
                ));
                onFollowChange?.({ isFollowing: false, targetPublicKey: targetKey });
            } else {
                await followSpace({
                    followerPublicKey: walletStr,
                    targetPublicKey: targetKey,
                    followerMuultiName,
                    targetMuultiName,
                });
                setIsFollowing(true);
                setFollowerCount((count) => (
                    typeof count === 'number' ? count + 1 : count
                ));
                onFollowChange?.({ isFollowing: true, targetPublicKey: targetKey });
            }
        } catch (err) {
            console.error('Follow update failed:', err);
            setError(err?.message || 'Unable to update follow.');
        } finally {
            setSaving(false);
        }
    };

    if (!targetKey || isSelf) return null;

    const buttonLabel = saving
        ? 'Saving...'
        : isFollowing
            ? 'Following'
            : 'Follow';
    const countLabel = showFollowerCount && typeof followerCount === 'number'
        ? `${followerCount} ${followerCount === 1 ? 'follower' : 'followers'}`
        : '';

    return (
        <span className={`space-follow-control${compact ? ' compact' : ''}${className ? ` ${className}` : ''}`}>
            <button
                type="button"
                className={`muulti-button space-follow-button${isFollowing ? ' is-following' : ''}`}
                onClick={handleToggleFollow}
                disabled={loading || saving}
            >
                {buttonLabel}
            </button>
            {countLabel ? <span className="space-follow-count">{countLabel}</span> : null}
            {error ? <span className="space-follow-error">{error}</span> : null}
        </span>
    );
}
